/* Colour lab.

   State is two handles — the phone finish and the PhoneBack colour — plus a size.
   Every combination is already in the DOM as a stacked image, so showing one is a
   class swap rather than a fetch: no blank frame between clicks.

   TWO SETS OF CONTROLS, ONE STATE. Chips on desktop, native <select>s on phones.
   Both are always in the markup and CSS picks; this only keeps them agreeing, so
   there is no breakpoint logic in JavaScript and nothing to rebuild on resize.

   THIS PAGE HAS THREE PICKERS AND NO SHARED STATE.
   Horizon's own <variant-picker> is display:none but live — the sticky bar reads
   and clicks its inputs. The picker a shopper actually sees is drawn by the
   Rubik Variant Images app in a <rubik-swatch> shadow root. And there is this
   section. None of them observe each other: the app writes to the theme, the
   theme never writes back to the app, and the sticky bar writes to the theme.

   So the lab writes to whichever picker is on screen (the app's, when present),
   and READS from neither. Reading from the app was the bug behind changing a
   colour in the sticky bar and watching this section keep the old one: the bar
   had driven the theme's hidden inputs, the app had not been told, and the lab
   was asking the app. The one thing every route agrees on is the variant id on
   the product form — it is what the cart posts and what the price and gallery
   follow — so that id is the state, and the chips, the dropdowns and the app's
   own label are all views reconciled back to it.

   WHY THE WRITES ARE SEQUENCED.
   Model and colour are separate controls and each triggers a section refetch
   (~1.5s and ~2.4s here). Fired together, the second landed on markup about to
   be replaced. Each write waits for the product form's variant id to change
   before the next starts; a click made mid-sequence is queued, not dropped.
   Reads wait for the same reason: a change made elsewhere is not visible on the
   form for a second or two, so reconciling on the event itself reads the value
   that is about to be replaced. It polls for a few seconds instead, and only
   after an external change — nothing runs on an idle page, which is the standing
   rule on this PDP.

   ADDING TO CART GOES THROUGH THE THEME'S OWN BUTTON.
   An earlier version posted to /cart/add.js itself and then tried to refresh the
   drawer by hand. It added the item correctly and broke everything around it:
   the refresh event was dispatched under the wrong name, so the drawer kept the
   markup it was rendered with at page load and showed a stale count — an empty
   cart, for a shopper whose cart had been empty — and the "open the drawer" step
   clicked the first button inside <cart-drawer-component>, which is the header's
   Open cart toggle, leaving the dialog open while the theme believed otherwise.
   The next press of the real Add to cart then did nothing. The lab now presses
   the theme's own Add to cart, exactly as a shopper would after choosing the
   same options, so drawer refresh, the cart count, analytics and any app
   listening for cart:update happen as the theme intends. */
(function () {
  'use strict';

  var root = document.querySelector('[data-scl]');
  if (!root) return;

  var main = document.getElementById('MainContent') || document.body;
  var stage = root.querySelector('.scl__stage');
  var form = root.querySelector('[data-scl-form]');
  var idField = root.querySelector('[data-scl-id]');
  var nameOut = root.querySelector('[data-scl-name]');
  var onOut = root.querySelector('[data-scl-on]');
  var priceOut = root.querySelector('[data-scl-price]');
  var ctaOut = root.querySelector('[data-scl-cta]');
  var opaque = root.querySelector('[data-scl-opaque]');
  var toast = root.querySelector('[data-scl-toast]');
  var toastText = root.querySelector('[data-scl-toast-text]');

  var phoneSelect = root.querySelector('[data-scl-phone-select]');
  var caseSelect = root.querySelector('[data-scl-case-select]');
  var phoneDot = root.querySelector('[data-scl-phone-dot]');
  var caseDot = root.querySelector('[data-scl-case-dot]');

  var MODEL_PRO = root.getAttribute('data-model-pro') || '';
  var MODEL_MAX = root.getAttribute('data-model-max') || '';
  var SETTLE_MS = 6000;

  var maxIds = {};
  try {
    var raw = root.querySelector('[data-scl-max]');
    if (raw) maxIds = JSON.parse(raw.textContent);
  } catch (e) {}

  var OPAQUE = /silicone|nanoleather|nanosuede|carbon-fiber/;

  var phone = null;
  var kase = null;
  var size = 'pro';
  var syncing = false;

  function pressed(sel, attr, val) {
    root.querySelectorAll(sel).forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute(attr) === val ? 'true' : 'false');
    });
  }

  function caseBtn(h) {
    return root.querySelector('[data-scl-case="' + (h || kase) + '"]');
  }

  function wantedId(job) {
    var btn = caseBtn(job.kase);
    if (!btn) return '';
    return String(job.size === 'max' ? (maxIds[job.kase] || '') : btn.getAttribute('data-pro'));
  }

  /* Keep the dropdowns and their dots in step with the chips. */
  function syncMobile() {
    if (phoneSelect && phone && phoneSelect.value !== phone) phoneSelect.value = phone;
    if (caseSelect && kase && caseSelect.value !== kase) caseSelect.value = kase;

    function paint(dot, select, isClear) {
      if (!dot || !select) return;
      var opt = select.options[select.selectedIndex];
      var sw = opt ? opt.getAttribute('data-swatch') : '';
      dot.classList.toggle('scl__dot--clear', !!isClear);
      dot.style.background = isClear ? '' : (sw || '');
    }
    paint(phoneDot, phoneSelect, false);
    paint(caseDot, caseSelect, kase === 'clear');
  }

  /* ---------- whichever picker is actually on screen ---------- */

  function pickerRoot() {
    var host = document.querySelector('rubik-swatch');
    if (host && host.shadowRoot && host.shadowRoot.querySelector('select, input')) {
      return host.shadowRoot;
    }
    return main;
  }

  function modelSelect() { return pickerRoot().querySelector('select'); }

  function colourFieldset() {
    var scope = pickerRoot();
    var sets = [].slice.call(scope.querySelectorAll('fieldset'));
    var hit = sets.filter(function (f) {
      var l = f.querySelector('legend');
      return l && /colou?r/i.test(l.textContent || '');
    })[0];
    return hit || sets[sets.length - 1] || null;
  }

  function colourInputs() {
    var fs = colourFieldset();
    if (fs) return [].slice.call(fs.querySelectorAll('input'));
    return [].slice.call(pickerRoot().querySelectorAll('input'));
  }

  function variantId() {
    var el = main.querySelector('product-form-component input[name="id"]');
    return el ? String(el.value) : '';
  }

  function realAddButton() {
    return main.querySelector('product-form-component [type="submit"]');
  }

  function settle(before) {
    return new Promise(function (resolve) {
      var t0 = Date.now();
      (function poll() {
        var v = variantId();
        if (v && v !== before) return setTimeout(resolve, 250);
        if (Date.now() - t0 > SETTLE_MS) return resolve();
        setTimeout(poll, 80);
      })();
    });
  }

  /* Repair what the shopper sees, without touching the variant. Only called once
     the variant is already correct, so it cannot change what is added.

     The legend holds two spans — the option's NAME and its VALUE — and they look
     alike. Writing into the first turned "Color: Frosted Black Sea Salt" into the
     value printed twice, so this matches the value span explicitly and does
     nothing when it cannot find one. */
  function repairDisplay(job) {
    var wantModel = job.size === 'max' ? MODEL_MAX : MODEL_PRO;
    var btn = caseBtn(job.kase);
    var wantColour = btn ? btn.getAttribute('data-name') : '';

    var sel = modelSelect();
    if (sel && wantModel && sel.value !== wantModel) {
      [].slice.call(sel.options).forEach(function (o) {
        var on = o.value === wantModel;
        o.selected = on;
        if (on) o.setAttribute('selected', 'selected');
        else o.removeAttribute('selected');
      });
      sel.value = wantModel;
    }

    colourInputs().forEach(function (i) {
      var on = i.value === wantColour;
      if (i.checked !== on) i.checked = on;
    });

    var fs = colourFieldset();
    var legend = fs && fs.querySelector('legend');
    var valueEl = legend && legend.querySelector(
      '.variant-option__swatch-value, [class*="option-name-value"], [class*="option-value"]'
    );
    if (valueEl && wantColour && valueEl.textContent.trim() !== wantColour) {
      valueEl.textContent = wantColour;
    }
  }

  /* ---------- push: lab -> product ---------- */

  var pendingJob = null;
  var running = false;
  var lastSeenId = '';

  function push() {
    pendingJob = { size: size, kase: kase };
    if (running) return;
    running = true;
    syncing = true;
    root.classList.add('is-syncing');
    step(0);
  }

  function done() {
    running = false;
    lastSeenId = variantId();
    root.classList.remove('is-syncing');
    setTimeout(function () { syncing = false; }, 300);
  }

  function step(tries) {
    var job = pendingJob;
    pendingJob = null;
    var btn = caseBtn(job.kase);
    if (!btn) return done();

    var wantModel = job.size === 'max' ? MODEL_MAX : MODEL_PRO;
    var wantColour = btn.getAttribute('data-name');
    var chain = Promise.resolve();

    var sel = modelSelect();
    if (sel && wantModel && sel.value !== wantModel) {
      var before = variantId();
      sel.value = wantModel;
      /* composed so the event escapes the app's shadow root. */
      sel.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      chain = chain.then(function () { return settle(before); });
    }

    chain = chain.then(function () {
      var input = colourInputs().filter(function (i) { return i.value === wantColour; })[0];
      if (!input || input.checked) return;
      var before2 = variantId();
      input.click();
      return settle(before2);
    });

    chain.then(function () {
      if (pendingJob) return step(0);

      var want = wantedId(job);
      if (want && variantId() === want) {
        repairDisplay(job);
        return done();
      }
      if (tries < 1) {
        pendingJob = job;
        return setTimeout(function () { step(tries + 1); }, 400);
      }
      done();
    });
  }

  /* Resolves true once the product form carries the variant the lab is showing.
     Pressing the theme's button is only equivalent to a shopper's own press if
     the picker has caught up first, so this is the guard on that assumption —
     and when it cannot be met, the caller posts the lab's own id instead of
     adding whatever happens to be selected. */
  function ensureSynced() {
    return new Promise(function (resolve) {
      var want = wantedId({ size: size, kase: kase });
      if (!want) return resolve(false);
      if (!running && variantId() === want) return resolve(true);
      push();
      var t0 = Date.now();
      (function poll() {
        if (!running && variantId() === want) return resolve(true);
        if (Date.now() - t0 > SETTLE_MS) return resolve(!running && variantId() === want);
        setTimeout(poll, 100);
      })();
    });
  }

  /* ---------- pull: variant id -> lab, and on to the app's label ---------- */

  /* The chips carry the Pro id; the Pro Max ids ride in a JSON script tag. That
     is the whole mapping, so a variant id resolves to a colour and a size
     without asking any picker what it thinks is selected. */
  function fromVariantId(id) {
    var btns = [].slice.call(root.querySelectorAll('[data-scl-case]'));
    for (var i = 0; i < btns.length; i++) {
      var h = btns[i].getAttribute('data-scl-case');
      if (String(btns[i].getAttribute('data-pro')) === id) return { kase: h, size: 'pro' };
      if (String(maxIds[h] || '') === id) return { kase: h, size: 'max' };
    }
    return null;
  }

  function reconcile() {
    if (running || syncing) return;
    var id = variantId();
    if (!id || id === lastSeenId) return;
    lastSeenId = id;

    var hit = fromVariantId(id);
    /* A model the lab does not cover — an iPhone 15, say. Leave it alone rather
       than snapping the chips to something the shopper did not choose. */
    if (!hit) return;

    var changed = false;
    if (hit.kase !== kase) { kase = hit.kase; pressed('[data-scl-case]', 'data-scl-case', kase); changed = true; }
    if (hit.size !== size) { size = hit.size; pressed('[data-scl-size]', 'data-scl-size', size); changed = true; }
    if (!changed) return;

    render(false);
    /* The change came from the theme's side, so the app's visible label is now
       showing the previous colour. Correct it to the variant that is actually
       in the form — the same repair the push path does. */
    repairDisplay({ size: size, kase: kase });
  }

  var watchTimer = null;
  function watch(ms) {
    var t0 = Date.now();
    clearInterval(watchTimer);
    watchTimer = setInterval(function () {
      reconcile();
      if (Date.now() - t0 > (ms || 4500)) clearInterval(watchTimer);
    }, 200);
  }

  /* ---------- render ---------- */

  function render(alsoPush) {
    var want = phone + '|' + kase;
    var found = false;
    stage.querySelectorAll('.scl__shot').forEach(function (img) {
      var on = img.getAttribute('data-shot') === want;
      img.classList.toggle('is-on', on);
      if (on) found = true;
    });
    if (!found && window.console) console.warn('[colour lab] no image for ' + want);

    syncMobile();

    var btn = caseBtn();
    if (!btn) return;

    var id = size === 'max' ? maxIds[kase] : btn.getAttribute('data-pro');
    if (idField) idField.value = id || btn.getAttribute('data-pro');

    var phoneBtn = root.querySelector('[data-scl-phone="' + phone + '"]');
    if (nameOut) nameOut.textContent = btn.getAttribute('data-name');
    if (onOut && phoneBtn) onOut.textContent = 'on ' + phoneBtn.getAttribute('title');
    if (priceOut) priceOut.textContent = btn.getAttribute('data-price');

    if (opaque) opaque.hidden = !OPAQUE.test(kase);

    if (alsoPush) push();
  }

  function say(msg) {
    if (!toast || !toastText) return;
    toastText.textContent = msg;
    toast.classList.add('is-on');
    clearTimeout(say._t);
    say._t = setTimeout(function () { toast.classList.remove('is-on'); }, 2600);
  }

  /* ---------- events ---------- */

  root.addEventListener('click', function (e) {
    var t = e.target.closest('[data-scl-phone], [data-scl-case], [data-scl-size]');
    if (!t) return;

    var touchesVariant = false;
    if (t.hasAttribute('data-scl-phone')) {
      phone = t.getAttribute('data-scl-phone');
      pressed('[data-scl-phone]', 'data-scl-phone', phone);
    } else if (t.hasAttribute('data-scl-case')) {
      kase = t.getAttribute('data-scl-case');
      pressed('[data-scl-case]', 'data-scl-case', kase);
      touchesVariant = true;
    } else {
      size = t.getAttribute('data-scl-size');
      pressed('[data-scl-size]', 'data-scl-size', size);
      touchesVariant = true;
    }

    /* The iPhone finish is a mockup, not a variant — changing it must not fire a
       variant refetch, which is the expensive part of this page. */
    render(touchesVariant);
  });

  if (phoneSelect) {
    phoneSelect.addEventListener('change', function () {
      phone = phoneSelect.value;
      pressed('[data-scl-phone]', 'data-scl-phone', phone);
      render(false);
    });
  }

  if (caseSelect) {
    caseSelect.addEventListener('change', function () {
      kase = caseSelect.value;
      pressed('[data-scl-case]', 'data-scl-case', kase);
      render(true);
    });
  }

  /* On document, not main: the app's events are composed and retarget to the
     <rubik-swatch> host, which sits inside main, but listening at the top also
     catches the theme's hidden picker, which is what the sticky bar clicks. */
  document.addEventListener('change', function (e) {
    if (root.contains(e.target)) return;
    watch(4500);
  });

  /* The sticky bar's colour menu and the app's swatches are buttons, and the
     theme's refetch follows the click rather than a change event on anything the
     lab can see. Watching after a click outside the lab costs one id comparison
     every 200ms for a few seconds and nothing at rest. */
  document.addEventListener('click', function (e) {
    if (root.contains(e.target)) return;
    watch(4500);
  }, true);

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var btn = form.querySelector('.scl__add');
      var label = ctaOut ? ctaOut.textContent : '';
      if (btn) btn.disabled = true;
      if (ctaOut) ctaOut.textContent = 'Adding…';

      function restore() {
        if (btn) btn.disabled = false;
        if (ctaOut) ctaOut.textContent = label;
      }

      ensureSynced().then(function (ok) {
        var real = ok ? realAddButton() : null;
        if (real) {
          real.click();
          setTimeout(restore, 1200);
          return;
        }

        /* The picker never caught up, or the theme's button is not on this page.
           Rather than press a button that would add the wrong variant, or hand
           the drawer a state it did not produce, post the lab's own id the plain
           way: the browser navigates to the cart, which is slower but correct
           and needs nothing of ours to stay in step. */
        if (!idField || !idField.value) { restore(); say('Please try again'); return; }
        form.setAttribute('action', '/cart/add');
        form.setAttribute('method', 'post');
        HTMLFormElement.prototype.submit.call(form);
      });
    });
  }

  var p0 = root.querySelector('[data-scl-phone][aria-pressed="true"]');
  var c0 = root.querySelector('[data-scl-case][aria-pressed="true"]');
  phone = p0 ? p0.getAttribute('data-scl-phone') : null;
  kase = c0 ? c0.getAttribute('data-scl-case') : null;
  if (phone && kase) {
    render(false);
    /* Landing on ?variant=... should show that combination, not the preset. */
    setTimeout(reconcile, 800);
  }
})();
